import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';

export interface ExportColumnsModalProps {
  isOpen: boolean;
  columns: string[];
  loading?: boolean;
  onConfirm: (selected: string[]) => void;
  onClose: () => void;
}

/**
 * Interim modal shown before an export runs. Lists every spreadsheet column
 * as a checkbox (all checked initially) so the user can choose which to
 * include. Confirms with the selected columns in canonical order.
 */
export function ExportColumnsModal({
  isOpen,
  columns,
  loading = false,
  onConfirm,
  onClose,
}: ExportColumnsModalProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(columns));

  // Reset to "all checked" whenever the column list changes or the modal opens.
  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(columns));
    }
  }, [isOpen, columns]);

  const allSelected = columns.length > 0 && selected.size === columns.length;
  const noneSelected = selected.size === 0;

  const orderedSelected = useMemo(
    () => columns.filter((c) => selected.has(c)),
    [columns, selected],
  );

  function toggle(column: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(column)) {
        next.delete(column);
      } else {
        next.add(column);
      }
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(columns));
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Choose columns to export"
      size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={noneSelected}
            onClick={() => onConfirm(orderedSelected)}
          >
            Export
          </Button>
        </>
      )}
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading columns...</span>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-slate-200 pb-3">
            <p className="text-sm text-slate-600">
              {selected.size} of {columns.length} columns selected
            </p>
            <button
              type="button"
              onClick={toggleAll}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
            {columns.map((column) => (
              <Checkbox
                key={column}
                label={column}
                checked={selected.has(column)}
                onChange={() => toggle(column)}
              />
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
